import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formsApi } from '../../../api/forms';
import {
  getCloseIfButtonClicked,
  getForm,
  getHideAfterSubmission,
  getHideIfButtonClicked,
  getTimeBetweenDisplayUnit,
  getTimeBetweenDisplayValue,
  getTimesDisplay,
  resetButtons,
  setCloseIfButtonClicked,
  setHideAfterSubmission,
  setHideIfButtonClicked,
  setTimeBetweenDisplayUnit,
  setTimeBetweenDisplayValue,
  setTimesDisplay,
} from '../../../store/reducers/campaign';
import { getIsStaticForm } from '../../../utils/form';
import TextWithDropdown from '../Components/TextWithDropdown';
import TextWithToggle from '../Components/TextWithToggle';
import TextWithValue from '../Components/TextWithValue';
import TextWithValueDropdown from '../Components/TextWithValueDropdown';
import { parseButtons } from '../helper';
import ABTesting from './ABTesting';
import styles from './AdvancedSettings.module.scss';

const periodUnits = [
  {
    label: 'days',
    value: 'days',
  },
  {
    label: 'hours',
    value: 'hours',
  },
  {
    label: 'minutes',
    value: 'minutes',
  },
];

const AdvancedSettings = () => {
  const dispatch = useDispatch();
  const [showContent, setShowContent] = useState(false);
  const [buttons, setButtons] = useState([]);
  const form = useSelector(getForm);
  const timesDisplay = useSelector(getTimesDisplay);
  const timeBetweenDisplayValue = useSelector(getTimeBetweenDisplayValue);
  const timeBetweenDisplayUnit = useSelector(getTimeBetweenDisplayUnit);
  const hideAfterSubmission = useSelector(getHideAfterSubmission);
  const hideIfButtonClicked = useSelector(getHideIfButtonClicked);
  const closeIfButtonClicked = useSelector(getCloseIfButtonClicked);

  const handleChange = (action, value) => {
    dispatch(action(value));
  };

  const {
    currentData: formData,
    isLoading: formLoading,
    isFetching: formFetching,
  } = formsApi.useGetFormByIdQuery(
    {
      id: form?.id,
    },
    { skip: !form }
  );

  const formDataLoading = formLoading || formFetching;

  const isStaticForm = getIsStaticForm(formData);

  useEffect(() => {
    if (formData && isStaticForm) {
      const buttonList = parseButtons(
        formData?.field_settings
          ?.find((page) => page.pageName === 'main-form')
          ?.fields.filter((f) => f.type === 'button')
      );

      setButtons(buttonList);

      const hideButtonInListOrNull =
        hideIfButtonClicked?.value === null ||
        buttonList.some(
          (button) =>
            button.value === hideIfButtonClicked?.value &&
            button.label === hideIfButtonClicked?.label &&
            button.index === hideIfButtonClicked?.index
        );

      const closeButtonInListOrNull =
        closeIfButtonClicked?.value === null ||
        buttonList.some(
          (button) =>
            button.value === closeIfButtonClicked?.value &&
            button.label === closeIfButtonClicked?.label &&
            button.index === closeIfButtonClicked?.index
        );

      if (!(hideButtonInListOrNull && closeButtonInListOrNull)) {
        if (buttonList.length > 0) {
          dispatch(setHideIfButtonClicked(buttonList[buttonList.length - 1]));
          dispatch(setCloseIfButtonClicked(buttonList[buttonList.length - 1]));
        }
      }
    } else if (formData) {
      dispatch(resetButtons());
    } else {
      setButtons([]);
    }
  }, [formData]);

  return (
    <>
      <button
        type="button"
        className={`${styles.button} ${showContent ? styles.opened : ''}`}
        onClick={() => setShowContent(!showContent)}
      >
        Advanced settings {showContent ? '-' : '+'}
      </button>
      {showContent && (
        <div className={styles.content}>
          <TextWithValue
            textBefore="Display the form up to"
            value={timesDisplay}
            textAfter="times"
            onChange={(newValue) =>
              handleChange(setTimesDisplay, Number(newValue))
            }
          />
          <TextWithValueDropdown
            text="Minimum time between displays"
            inputValue={timeBetweenDisplayValue}
            onInputChange={(newValue) =>
              handleChange(setTimeBetweenDisplayValue, Number(newValue))
            }
            dropDownValue={timeBetweenDisplayUnit}
            onDropdownChange={(newValue) =>
              handleChange(setTimeBetweenDisplayUnit, newValue)
            }
            dropdownData={periodUnits}
          />
          {form && !formDataLoading && isStaticForm && buttons.length > 0 ? (
            <>
              <TextWithDropdown
                text="Do not show again if button is clicked"
                value={hideIfButtonClicked}
                data={buttons}
                onChange={(newValue) =>
                  handleChange(setHideIfButtonClicked, newValue)
                }
                nullable
                nullableText="Disabled"
              />
              <TextWithDropdown
                text="Close the modal if button is clicked"
                value={closeIfButtonClicked}
                data={buttons}
                onChange={(newValue) =>
                  handleChange(setCloseIfButtonClicked, newValue)
                }
                nullable
                nullableText="Disabled"
              />
            </>
          ) : null}
          {form && !formDataLoading && !isStaticForm ? (
            <TextWithToggle
              text="Do not show again if submitted"
              isChecked={hideAfterSubmission}
              onChange={(newValue) =>
                handleChange(setHideAfterSubmission, newValue)
              }
            />
          ) : null}
          <ABTesting />
        </div>
      )}
    </>
  );
};

export default AdvancedSettings;
